/**
 * Accounts receivable: aging, funding, and collection risk (KAN-38 / KAN-55).
 *
 * AR is *derived*, not stored. Unlike the billing forecast — which is
 * snapshotted so "generated before the cycle closed" can be a fact about the
 * data — a receivable balance has no useful history of its own: the balance is
 * whatever the invoices say right now, and those invoices already carry their
 * own provenance and their own payment rows. Snapshotting it would create a
 * second version of the truth for Finance to reconcile against the first.
 *
 * Everything here is therefore a pure function of invoice and payment rows plus
 * the rule the reader chose, which is what makes that rule *configurable* in
 * the sense KAN-55 asks for: it travels with the request rather than living in
 * a table, so a link reproduces exactly the flagging the sender saw.
 */
/**
 * Aging buckets, by days past due.
 *
 * KAN-55 names `0-30/31-60/61-90/90+`, and two further buckets sit outside that
 * list for reasons that would otherwise be hidden inside one of them:
 *
 *  - `not_yet_due` is a receivable that is not late. An invoice issued
 *    yesterday on 30-day terms carries its full balance, and folding it into
 *    `0-30` would report the firm as overdue on money nobody has been asked
 *    for yet — the single most misread figure on an AR report.
 *  - `undated` is an invoice with no due date recorded. It cannot be aged, and
 *    both plausible fallbacks — treat as due on issue, treat as not yet due —
 *    are guesses that move money between buckets silently. It is reported on
 *    its own so the gap is visible and fixable upstream.
 *
 * Neither is counted as overdue anywhere. `OVERDUE_BUCKETS` is the list that
 * adds up to overdue AR.
 */
export const AGING_BUCKETS = [
    'not_yet_due',
    'days_0_30',
    'days_31_60',
    'days_61_90',
    'days_90_plus',
    'undated',
];
/** The buckets that mean "late". Overdue AR is the sum of exactly these. */
export const OVERDUE_BUCKETS = [
    'days_0_30',
    'days_31_60',
    'days_61_90',
    'days_90_plus',
];
export const AGING_BUCKET_META = {
    not_yet_due: {
        label: 'Not yet due',
        description: 'Issued and outstanding, but the due date has not passed. Not overdue.',
        fromDays: null,
        toDays: null,
    },
    days_0_30: {
        label: '0-30 days',
        description: 'Up to a month past due. Usually still within the normal payment rhythm.',
        fromDays: 0,
        toDays: 30,
    },
    days_31_60: {
        label: '31-60 days',
        description: 'A full billing cycle has passed since the due date.',
        fromDays: 31,
        toDays: 60,
    },
    days_61_90: {
        label: '61-90 days',
        description: 'Two cycles late. Usually needs a conversation rather than a reminder.',
        fromDays: 61,
        toDays: 90,
    },
    days_90_plus: {
        label: '90+ days',
        description: 'Over a quarter late. Historically the least likely to be collected in full.',
        fromDays: 91,
        toDays: null,
    },
    undated: {
        label: 'No due date',
        description: 'Outstanding with no due date recorded upstream, so it cannot be aged. Held out of every overdue figure rather than guessed into a bucket.',
        fromDays: null,
        toDays: null,
    },
};
/**
 * Which bucket a balance falls in.
 *
 * `daysOverdue` is whole days past the due date, and null means no due date was
 * recorded — not zero. That distinction is the whole point of the `undated`
 * bucket, so this takes null rather than letting a caller coalesce it away.
 */
export function agingBucketFor(daysOverdue) {
    if (daysOverdue === null)
        return 'undated';
    if (daysOverdue <= 0)
        return 'not_yet_due';
    if (daysOverdue <= 30)
        return 'days_0_30';
    if (daysOverdue <= 60)
        return 'days_31_60';
    if (daysOverdue <= 90)
        return 'days_61_90';
    return 'days_90_plus';
}
/** An empty bucket breakdown, for accumulating into. */
export function emptyBuckets() {
    return {
        not_yet_due: 0,
        days_0_30: 0,
        days_31_60: 0,
        days_61_90: 0,
        days_90_plus: 0,
        undated: 0,
    };
}
/** The part of a bucket breakdown that is actually late. */
export function overdueTotal(buckets) {
    return OVERDUE_BUCKETS.reduce((total, bucket) => total + buckets[bucket], 0);
}
/**
 * How much of a receivable is already covered by money the firm holds.
 *
 * "Funding" here means trust: a $10,000 balance on a matter with $10,000 in
 * trust is a transfer waiting to be posted, not a collection problem, and
 * treating the two alike is how a collections list fills with accounts that owe
 * nothing in practice.
 *
 * Trust is read as it stands now, never as of a past date, for the same reason
 * the forecast does — see `services/billing_forecast.ts`. A balance that
 * existed in July may have paid August's bill, and offering it against July's
 * receivable would spend the same dollar twice.
 */
export const FUNDING_STATUSES = ['funded', 'partially_funded', 'unfunded'];
export const FUNDING_STATUS_META = {
    funded: {
        label: 'Funded',
        description: 'Trust held covers the outstanding balance in full. A transfer to post, not a collection.',
    },
    partially_funded: {
        label: 'Part funded',
        description: 'Trust covers some of the balance. The remainder has to be collected.',
    },
    unfunded: {
        label: 'Unfunded',
        description: 'No trust held against this balance. All of it has to be collected.',
    },
};
export function fundingStatusFor(outstandingMinor, trustAvailableMinor) {
    if (trustAvailableMinor <= 0)
        return 'unfunded';
    return trustAvailableMinor >= outstandingMinor ? 'funded' : 'partially_funded';
}
/**
 * The firm's starting rule: 60 days late on $5,000 or more.
 *
 * Sixty days because that is two billing cycles — one missed cycle is common
 * and self-correcting, two is a pattern. $5,000 because below that the cost of
 * a collections conversation is a material fraction of the amount. Both are
 * defaults rather than policy: the rule is sent with every request, and the
 * screen shows which numbers produced the flags.
 */
export const DEFAULT_AR_RISK_RULE = {
    minDaysOverdue: 60,
    minBalanceMinor: 500_000,
};
/**
 * Risk levels, worst last so the tuple reads as an ordering.
 *
 * `watch` is not a second invented threshold — it is exactly "overdue, but not
 * by enough to meet the rule". Naming it keeps the merely-late accounts visible
 * as a group instead of leaving them indistinguishable from accounts with
 * nothing late at all.
 */
export const AR_RISK_LEVELS = ['none', 'watch', 'high'];
export const AR_RISK_META = {
    high: {
        label: 'High risk',
        description: 'Meets both halves of the risk rule: old enough and large enough.',
    },
    watch: {
        label: 'Overdue',
        description: "Something is past due, but under the risk rule's thresholds.",
    },
    none: {
        label: 'Current',
        description: 'Nothing past due. The balance is outstanding but not late.',
    },
};
/** Applies the rule to one account's overdue position. */
export function assessArRisk(overdueMinor, maxDaysOverdue, rule) {
    if (overdueMinor <= 0)
        return 'none';
    return maxDaysOverdue >= rule.minDaysOverdue && overdueMinor >= rule.minBalanceMinor
        ? 'high'
        : 'watch';
}
/** How AR lines are grouped. Each grouping answers a different question. */
export const AR_GROUPINGS = ['client', 'matter', 'attorney'];
export const AR_GROUPING_META = {
    client: {
        label: 'Client',
        description: 'Who owes the firm. Every outstanding invoice belongs to exactly one client.',
    },
    matter: {
        label: 'Matter',
        description: 'What the money was billed for. Invoices raised at client level appear as one unattributed row.',
    },
    attorney: {
        label: 'Responsible attorney',
        description: 'Who is accountable for chasing it, via the matter. Invoices with no matter, or a matter with no responsible attorney, appear as one unassigned row.',
    },
};
/**
 * Orderings for the AR table.
 *
 * `risk` is the default and is a documented ordering rather than a score: risk
 * level first, then overdue balance, then age. No weighted priority index is
 * computed anywhere in this feature — a number built from invented
 * coefficients looks authoritative and cannot be checked, which is the same
 * objection this codebase makes to an invented realization haircut.
 */
export const AR_SORTS = ['risk', 'overdue', 'oldest', 'outstanding'];
export const AR_SORT_META = {
    risk: {
        label: 'Collection priority',
        description: 'High risk first, then by overdue balance, then by age. No weighted score.',
    },
    overdue: { label: 'Overdue balance', description: 'Largest amount past due first.' },
    oldest: { label: 'Age', description: 'Oldest overdue invoice first.' },
    outstanding: {
        label: 'Total outstanding',
        description: 'Largest balance first, whether or not any of it is late.',
    },
};
/* ------------------------------------------------------------------------ *
 * Payment commitments (KAN-88)
 * ------------------------------------------------------------------------ */
/**
 * What became of a promise.
 *
 * `withdrawn` exists so that a commitment somebody records and the client then
 * renegotiates does not have to be called `missed`. A collections history where
 * every superseded promise reads as a broken one is a history nobody will
 * record honestly.
 */
export const PAYMENT_COMMITMENT_STATUSES = ['open', 'kept', 'missed', 'withdrawn'];
export const PAYMENT_COMMITMENT_STATUS_LABELS = {
    open: 'Open',
    kept: 'Kept',
    missed: 'Missed',
    withdrawn: 'Withdrawn',
};
/**
 * Trailing window for "is this client paying at all".
 *
 * Ninety days rather than a year: the question a collections list asks is
 * whether money has moved recently, and a payment eleven months ago answers a
 * different one.
 */
export const PAYMENT_HISTORY_DAYS = 90;
/**
 * Result of reconciling this platform's AR against QuickBooks Online (KAN-55:
 * "AR totals reconcile with QuickBooks Online AR").
 *
 * `unavailable` is a first-class outcome rather than an error, and it stays one
 * now that QuickBooks is connected. Invoices here are pulled from Clio; QBO is
 * the accounting system of record and is read *live* at the moment of
 * comparison, so the comparison depends on a request that can fail — Intuit
 * returning a 5xx, a rate limit, an expired connection — every time it is
 * asked. When it does, the honest answer is that the comparison could not be
 * made, naming what is missing. A variance of zero would read as agreement.
 */
export const AR_RECONCILIATION_STATUSES = ['reconciled', 'variance', 'unavailable'];
/**
 * How far apart the two systems may be and still count as reconciled.
 *
 * Zero. Both sides are integer minor units read from their own system of
 * record, so there is no rounding for a tolerance to absorb — it would only
 * hide the first cent of a real difference, and a one-cent gap in AR is exactly
 * the kind of thing that turns out to be a whole invoice posted twice.
 */
export const AR_RECONCILIATION_TOLERANCE_MINOR = 0;
//# sourceMappingURL=ar.js.map